import { useState, useRef, useEffect, useCallback } from "react";
import {
    SearchIcon,
    UploadIcon,
    FileTextIcon,
    Trash2Icon,
    BookOpenIcon,
    DatabaseIcon,
    AlertCircleIcon,
    XIcon,
    CheckCircle2Icon,
    Loader2Icon,
} from "lucide-react";
import TrainingAuthLayout from "@container/layout/training-auth";
import { Card } from "@component/card";
import { Button } from "@component/button";
import { Input } from "@component/input";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@component/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@component/select";
import { StatTile } from "@component/training/stat-tile";
import { EmptyState } from "@component/empty-state";
import { Pagination } from "@component/pagination";
import {
    KnowledgeDocDrawer,
    CategoryChip,
} from "@component/training/knowledge-doc-drawer";
import { useKbUpload } from "@context/kb_upload_context";
import { useUserProfileContext } from "@context/user_profile";
import { cn } from "@util/tailwind";
import {
    listKbDocuments,
    deleteKbDocument,
    listKbUploaders,
    KB_SCOPES_ENABLED,
    type KbDocument,
    type KbUploader,
    type KbStats,
    type KbScope,
} from "@service/knowledge-base";

/** Scope chip for the master view (Phase 1). Absent scopes ⇒ workspace. */
function ScopeChip({ scope }: { scope: KbScope }) {
    const isCampaign = scope.type === "campaign";
    // Label each scope type explicitly. Previously everything that wasn't a
    // campaign fell through to "Workspace", so an aiqm_batch attachment was
    // mislabelled as Workspace. ScopeCell already filters aiqm_batch out of the
    // master list, but keep this correct so no scope type can ever be mislabelled.
    const label =
        scope.type === "campaign"
            ? (scope.name ?? "Campaign")
            : scope.type === "aiqm_batch"
              ? "AI QM"
              : "Workspace";
    return (
        <span
            className={cn(
                "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border max-w-[180px]",
                isCampaign
                    ? "bg-violet-50 text-violet-700 border-violet-200"
                    : "bg-gray-50 text-gray-600 border-gray-200",
            )}
            title={isCampaign && scope.name ? `Campaign: ${scope.name}` : undefined}
        >
            <span className="truncate">{label}</span>
        </span>
    );
}

// How many campaign chips to show before collapsing the rest into "+N more".
const CAMPAIGN_CHIP_CAP = 2;

/** The Scope column cell. Shows only the MANAGED scopes — Workspace and
 *  Campaign. AIQM-batch attachments are intentionally omitted: a batch is a
 *  transient, per-upload grouping that isn't managed from this page, and
 *  surfacing one chip per batch would clutter the column. Campaign chips beyond
 *  CAMPAIGN_CHIP_CAP collapse into a "+N more" chip so a document attached to
 *  many campaigns doesn't blow up the row height. */
function ScopeCell({ scopes }: { scopes?: KbScope[] }) {
    // Legacy documents (no `scopes` field at all) predate scoping and are
    // workspace-wide. An empty array means "has attachments, none managed here".
    if (scopes === undefined) return <ScopeChip scope={{ type: "workspace" }} />;

    const workspace = scopes.filter((s) => s.type === "workspace");
    const campaigns = scopes.filter((s) => s.type === "campaign");

    if (workspace.length === 0 && campaigns.length === 0) {
        // Doc exists only in AIQM upload(s) — no managed scope to show here.
        return (
            <span className="text-xs text-gray-400" title="Used in AI QM uploads only">
                —
            </span>
        );
    }

    const shownCampaigns = campaigns.slice(0, CAMPAIGN_CHIP_CAP);
    const hidden = campaigns.slice(CAMPAIGN_CHIP_CAP);

    return (
        <div className="flex flex-wrap gap-1">
            {workspace.map((s, i) => (
                <ScopeChip key={`ws:${i}`} scope={s} />
            ))}
            {shownCampaigns.map((s, i) => (
                <ScopeChip key={`camp:${s.id ?? i}`} scope={s} />
            ))}
            {hidden.length > 0 && (
                <span
                    className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border bg-violet-50 text-violet-700 border-violet-200 cursor-default"
                    title={hidden.map((s) => s.name ?? "Campaign").join(", ")}
                >
                    +{hidden.length} more
                </span>
            )}
        </div>
    );
}

const MAX_TOTAL_SIZE = 100 * 1024 * 1024; // 100 MB
// PDF only (product decision 2026-07-16): DOCX was accepted but never
// text-extracted BE-side, so it silently contributed nothing to evaluation.
// Existing DOCX rows still render/list; only new uploads are restricted.
const ACCEPTED_EXTENSIONS = [".pdf"];
const ACCEPTED_MIME_TYPES = ["application/pdf"];
const PAGE_SIZE = 20;

function formatFileSize(bytes: number): string {
    if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`;
    return `${Math.round(bytes / 1_000)} KB`;
}

function formatDate(iso: string): string {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
    });
}

function getInitials(name: string): string {
    return name
        .split(" ")
        .map((p) => p[0])
        .slice(0, 2)
        .join("")
        .toUpperCase();
}

function validateFile(file: File): string | null {
    const ext = `.${file.name.split(".").pop()?.toLowerCase()}`;
    if (!ACCEPTED_EXTENSIONS.includes(ext) && !ACCEPTED_MIME_TYPES.includes(file.type)) {
        return "Unsupported format. Use PDF.";
    }
    return null;
}

interface QueuedFile {
    id: string;
    file: File;
    errorMessage?: string;
}

function KnowledgeBasePageContent() {
    const { beginUpload, isProcessing, flashToast, uploadCompletedCount } = useKbUpload();
    const [, currentUser] = useUserProfileContext();
    // Members are read-only — they can browse/preview but not upload or delete.
    const canWrite = currentUser?.permission !== "member";

    const [search, setSearch] = useState("");
    const [uploaderFilter, setUploaderFilter] = useState("all");
    const [scopeFilter, setScopeFilter] = useState<"all" | "workspace" | "campaign">("all");
    const [page, setPage] = useState(1);

    // Server-driven data (BE #3112): list is paginated/filtered server-side, and
    // `stats` is workspace-wide (ignores filters) so the tiles stay stable.
    const [documents, setDocuments] = useState<KbDocument[]>([]);
    const [total, setTotal] = useState(0);
    const [stats, setStats] = useState<KbStats>({ totalDocuments: 0, storageUsedBytes: 0 });
    const [uploaderList, setUploaderList] = useState<KbUploader[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    // Bumped to force a list refetch after an upload/delete without changing filters.
    const [reloadKey, setReloadKey] = useState(0);
    const [deleteTarget, setDeleteTarget] = useState<KbDocument | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);
    // Opens the shared KnowledgeDocDrawer (Preview / Extracted Information).
    const [previewTarget, setPreviewTarget] = useState<KbDocument | null>(null);

    // Upload modal state
    const [uploadOpen, setUploadOpen] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [queuedFiles, setQueuedFiles] = useState<QueuedFile[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const totalQueuedSize = queuedFiles.reduce((s, f) => s + f.file.size, 0);
    const isBatchTooLarge = totalQueuedSize > MAX_TOTAL_SIZE;
    const hasFiles = queuedFiles.length > 0;
    const canUpload = hasFiles && !isBatchTooLarge;

    function addFiles(fileList: FileList) {
        const incoming: QueuedFile[] = [];
        for (const file of Array.from(fileList)) {
            const error = validateFile(file);
            incoming.push({
                id: `${file.name}:${file.size}:${Date.now()}:${Math.random()}`,
                file,
                errorMessage: error ?? undefined,
            });
        }
        setQueuedFiles((prev) => [...prev, ...incoming]);
    }

    function removeQueued(id: string) {
        setQueuedFiles((prev) => prev.filter((f) => f.id !== id));
    }

    function clearFiles() {
        setQueuedFiles([]);
    }

    function resetUploadModal() {
        setQueuedFiles([]);
        setIsDragging(false);
    }

    function handleDragOver(e: React.DragEvent) {
        e.preventDefault();
        setIsDragging(true);
    }
    function handleDragLeave(e: React.DragEvent) {
        e.preventDefault();
        setIsDragging(false);
    }
    function handleDrop(e: React.DragEvent) {
        e.preventDefault();
        setIsDragging(false);
        if (e.dataTransfer.files.length > 0) addFiles(e.dataTransfer.files);
    }

    function handleUpload() {
        const valid = queuedFiles.filter((f) => !f.errorMessage);
        if (valid.length === 0 || isBatchTooLarge) return;
        beginUpload(valid.map((f) => f.file));
        setUploadOpen(false);
        resetUploadModal();
    }

    // Fetch the document list for the current page/search/uploader filter.
    const refresh = useCallback(async () => {
        setIsLoading(true);
        setLoadError(false);
        try {
            const res = await listKbDocuments({
                page,
                page_size: PAGE_SIZE,
                search: search.trim() || undefined,
                uploaded_by: uploaderFilter !== "all" ? uploaderFilter : undefined,
                scope: KB_SCOPES_ENABLED && scopeFilter !== "all" ? scopeFilter : undefined,
            });
            setDocuments(res.documents);
            setTotal(res.total);
            setStats(res.stats);
        } catch {
            // A real error (auth/network/500) must read as "couldn't load", not
            // as a legitimately empty knowledge base.
            setLoadError(true);
            setDocuments([]);
            setTotal(0);
        } finally {
            setIsLoading(false);
        }
    }, [page, search, uploaderFilter, scopeFilter]);

    // Debounce so fast typing in the search box doesn't fire a request per key.
    // `reloadKey` is a trigger: bumping it after an upload/delete re-runs this
    // effect (and thus refetches) without any filter having changed.
    useEffect(() => {
        const t = window.setTimeout(refresh, 250);
        return () => window.clearTimeout(t);
    }, [refresh, reloadKey]);

    // Uploaders for the filter dropdown. Errors → hide the filter (empty list)
    // rather than render a misleading empty dropdown.
    const refreshUploaders = useCallback(() => {
        listKbUploaders()
            .then(setUploaderList)
            .catch(() => setUploaderList([]));
    }, []);
    useEffect(() => {
        refreshUploaders();
    }, [refreshUploaders]);

    // When an upload batch finishes, jump to page 1 and pull in the new docs.
    useEffect(() => {
        if (uploadCompletedCount === 0) return;
        setPage(1);
        setReloadKey((k) => k + 1);
        refreshUploaders();
    }, [uploadCompletedCount, refreshUploaders]);

    async function handleDelete(doc: KbDocument) {
        setDeleting(true);
        setDeleteError(null);
        try {
            await deleteKbDocument(doc.id);
            // Success: close the dialog and refetch so the row disappears.
            setDeleteTarget(null);
            setReloadKey((k) => k + 1);
            refreshUploaders();
        } catch (err) {
            // Keep the dialog open and say WHY — a silently reappearing row reads
            // as a glitch, not a failure. Map the common cases; fall back to a
            // generic message for the rest (e.g. the current staging 500).
            const status = (err as { response?: { status?: number } })?.response
                ?.status;
            setDeleteError(
                status === 403
                    ? "You don't have permission to delete this document."
                    : status === 409
                      ? "This document can't be deleted because it's referenced by existing results."
                      : "Something went wrong deleting this document. Please try again, or contact support if it persists.",
            );
        } finally {
            setDeleting(false);
        }
    }

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    // The list is already paginated + filtered server-side.
    const paginated = documents;

    const totalDocs = stats.totalDocuments;
    const totalStorageBytes = stats.storageUsedBytes;

    const hasActiveFilter = Boolean(search) || uploaderFilter !== "all" || scopeFilter !== "all";

    function resetFilters() {
        setSearch("");
        setUploaderFilter("all");
        setScopeFilter("all");
        setPage(1);
    }

    return (
        <>
            {/* Page header */}
            <div className="flex items-start justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-900">Knowledge Base</h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Upload reference documents for AI evaluation across your workspace.
                    </p>
                </div>
                {canWrite && (
                    <Button
                        onClick={() => {
                            if (isProcessing) { flashToast(); return; }
                            setUploadOpen(true);
                        }}
                        className={cn("flex-shrink-0 mt-1", isProcessing && "opacity-60 cursor-not-allowed")}
                    >
                        <UploadIcon className="w-4 h-4" />
                        {isProcessing ? "Uploading…" : "Upload Documents"}
                    </Button>
                )}
            </div>

            {/* Stat tiles */}
            <div className="grid grid-cols-2 gap-4 mb-6">
                <StatTile
                    label="Total Documents"
                    value={totalDocs}
                    icon={BookOpenIcon}
                    tooltip="Total number of documents uploaded to this workspace's knowledge base."
                />
                <StatTile
                    label="Storage Used"
                    value={formatFileSize(totalStorageBytes)}
                    badge={`of ${formatFileSize(MAX_TOTAL_SIZE)}`}
                    icon={DatabaseIcon}
                    tooltip="Combined size of all successfully uploaded documents, out of the 100 MB workspace limit."
                />
            </div>

            {/* Search + filters */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <div className="relative flex-1 min-w-0">
                    <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                        placeholder="Search documents…"
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                        className="pl-9"
                    />
                </div>
                {KB_SCOPES_ENABLED && (
                    <Select
                        value={scopeFilter}
                        onValueChange={(v) => {
                            setScopeFilter(v as "all" | "workspace" | "campaign");
                            setPage(1);
                        }}
                    >
                        <SelectTrigger className="w-[170px] text-sm flex-shrink-0">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Scopes</SelectItem>
                            <SelectItem value="workspace">Workspace</SelectItem>
                            <SelectItem value="campaign">Campaign-scoped</SelectItem>
                        </SelectContent>
                    </Select>
                )}
                <Select
                    value={uploaderFilter}
                    onValueChange={(v) => { setUploaderFilter(v); setPage(1); }}
                >
                    <SelectTrigger className="w-[180px] text-sm flex-shrink-0">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">
                            All Uploaders{" "}
                            <span className="text-gray-400">({totalDocs})</span>
                        </SelectItem>
                        {uploaderList.map((u) => (
                            <SelectItem key={u.id} value={u.id}>
                                {u.name}
                                {typeof u.count === "number" && (
                                    <span className="text-gray-400">{" "}({u.count})</span>
                                )}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Table, loading, error, or empty state */}
            {isLoading && documents.length === 0 ? (
                <Card className="overflow-hidden">
                    <div className="flex flex-col items-center justify-center py-20 text-center">
                        <Loader2Icon className="h-8 w-8 text-gray-300 animate-spin" />
                        <p className="text-sm text-gray-500 mt-3">Loading documents…</p>
                    </div>
                </Card>
            ) : loadError ? (
                <Card className="overflow-hidden">
                    <EmptyState
                        icon={<AlertCircleIcon className="h-12 w-12 text-gray-300" />}
                        title="Couldn't load documents"
                        description="Something went wrong loading your knowledge base. Please try again."
                        action={
                            <Button variant="outline" onClick={() => setReloadKey((k) => k + 1)}>
                                Retry
                            </Button>
                        }
                    />
                </Card>
            ) : documents.length === 0 ? (
                <Card className="overflow-hidden">
                    <EmptyState
                        icon={<BookOpenIcon className="h-12 w-12 text-gray-300" />}
                        title={
                            hasActiveFilter
                                ? "No documents match your filters"
                                : "No documents yet"
                        }
                        description={
                            hasActiveFilter ? (
                                <span>
                                    Try adjusting your search or filters.{" "}
                                    <button
                                        onClick={resetFilters}
                                        className="text-blue-600 hover:underline"
                                    >
                                        Clear filters
                                    </button>
                                </span>
                            ) : canWrite ? (
                                "Upload your first document to give the AI evaluator reference material for your workspace. Supported: PDF."
                            ) : (
                                "No documents have been uploaded to your workspace yet."
                            )
                        }
                        action={
                            !hasActiveFilter && canWrite ? (
                                <Button onClick={() => setUploadOpen(true)}>
                                    <UploadIcon className="w-4 h-4" />
                                    Upload Documents
                                </Button>
                            ) : undefined
                        }
                    />
                </Card>
            ) : (
                <Card className="overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-max">
                            <thead>
                                <tr className="bg-gray-50 border-b border-gray-200">
                                    <th className="h-[45px] px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap text-left">
                                        Document
                                    </th>
                                    {KB_SCOPES_ENABLED && (
                                        <th className="h-[45px] px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap text-left">
                                            Scope
                                        </th>
                                    )}
                                    {/* "Related categories", not "Informs": the category tags are a
                                        browsing aid assigned after upload — the evaluator does not
                                        restrict a document to them, so the header must not claim
                                        scoring influence (Trainer KB Phase 0 truthfulness fix). */}
                                    <th className="h-[45px] px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap text-left">
                                        Related categories
                                    </th>
                                    <th className="h-[45px] px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap text-center">
                                        Uploaded by
                                    </th>
                                    <th className="h-[45px] px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap text-center">
                                        Date uploaded
                                    </th>
                                    <th className="h-[45px] px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap text-center">
                                        Status
                                    </th>
                                    {canWrite && <th className="h-[45px] px-4 py-3 w-px" />}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {paginated.map((doc) => (
                                    <tr
                                        key={doc.id}
                                        className="hover:bg-gray-50 transition-colors cursor-pointer"
                                        onClick={() => setPreviewTarget(doc)}
                                    >
                                        {/* Document — name + file type + size as subtext */}
                                        <td className="px-4 py-4 whitespace-nowrap">
                                            <div className="flex items-center gap-3">
                                                <div className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-lg bg-blue-50">
                                                    <FileTextIcon className="w-4 h-4 text-blue-600" />
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="text-sm font-medium text-gray-900 truncate">
                                                        {doc.name}
                                                    </div>
                                                    <div className="text-xs text-gray-500 mt-0.5">
                                                        {doc.fileType} · {formatFileSize(doc.sizeBytes)}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>

                                        {/* Scope — where this doc is attached (Phase 1) */}
                                        {KB_SCOPES_ENABLED && (
                                            <td className="px-4 py-4">
                                                <ScopeCell scopes={doc.scopes} />
                                            </td>
                                        )}

                                        {/* Related categories — browsing aid, not a scoring claim */}
                                        <td className="px-4 py-4">
                                            <div className="flex flex-wrap gap-1">
                                                {doc.influences && doc.influences.length > 0 ? (
                                                    doc.influences.map((el) => (
                                                        <CategoryChip key={el} element={el} />
                                                    ))
                                                ) : (
                                                    <span className="text-gray-400">—</span>
                                                )}
                                            </div>
                                        </td>

                                        {/* Uploaded by */}
                                        <td className="px-4 py-4 whitespace-nowrap text-center">
                                            <div className="flex items-center justify-center gap-2">
                                                <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-semibold flex-shrink-0">
                                                    {getInitials(doc.uploadedBy.name)}
                                                </div>
                                                <span className="text-sm text-gray-700">
                                                    {doc.uploadedBy.name}
                                                </span>
                                            </div>
                                        </td>

                                        {/* Date */}
                                        <td className="px-4 py-4 whitespace-nowrap text-center text-sm text-gray-500">
                                            {formatDate(doc.uploadedAt)}
                                        </td>

                                        {/* Status — rightmost, no icon/dot */}
                                        <td className="px-4 py-4 whitespace-nowrap text-center">
                                            {doc.status === "processing" ? (
                                                <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border bg-amber-50 text-amber-700 border-amber-200">
                                                    Processing
                                                </span>
                                            ) : doc.status === "failed" ? (
                                                <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border bg-rose-50 text-rose-700 border-rose-200">
                                                    Failed
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border bg-emerald-50 text-emerald-700 border-emerald-200">
                                                    Completed
                                                </span>
                                            )}
                                        </td>

                                        {/* Delete action — no header; hidden for members */}
                                        {canWrite && (
                                            <td className="px-4 py-4 whitespace-nowrap text-center">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDeleteTarget(doc);
                                                    }}
                                                    title="Delete document"
                                                    className="w-8 h-8 rounded-md flex items-center justify-center text-gray-400 hover:bg-rose-50 hover:text-rose-600 transition-colors"
                                                >
                                                    <Trash2Icon className="w-4 h-4" />
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pagination
                        currentPage={page}
                        totalPages={totalPages}
                        total={total}
                        pageSize={PAGE_SIZE}
                        itemLabel="document"
                        onPageChange={setPage}
                        className="mt-0 border-t border-gray-200"
                    />
                </Card>
            )}

            {/* ===== Upload modal — 2-panel ===== */}
            <Dialog
                open={uploadOpen}
                onOpenChange={(open) => {
                    if (!open) resetUploadModal();
                    setUploadOpen(open);
                }}
            >
                <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <div className="flex items-center gap-3">
                            <div className="rounded-full bg-blue-100 p-2">
                                <UploadIcon className="w-4 h-4 text-blue-600" />
                            </div>
                            <div>
                                <DialogTitle>Upload Documents</DialogTitle>
                                <p className="text-xs text-gray-500 mt-0.5">
                                    PDF · Up to 100 MB total
                                </p>
                            </div>
                        </div>
                    </DialogHeader>

                    <div className="flex-1 overflow-y-auto py-2 -mx-6 px-6">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Left panel — drag & drop */}
                            <div className="flex flex-col">
                                <div
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={handleDrop}
                                    onClick={() => fileInputRef.current?.click()}
                                    className={cn(
                                        "flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 flex-1 text-center transition-colors cursor-pointer min-h-[220px]",
                                        isDragging
                                            ? "border-blue-500 bg-blue-50"
                                            : "border-gray-300 hover:border-blue-400 hover:bg-gray-50",
                                    )}
                                >
                                    <UploadIcon className="w-12 h-12 mb-4 text-gray-300" />
                                    <p className="text-base font-semibold text-gray-700 mb-1">
                                        Drag &amp; drop files here
                                    </p>
                                    <p className="text-sm text-gray-500 mb-5">
                                        or click to browse files
                                    </p>
                                    <div className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-600 bg-white hover:bg-gray-50 transition-colors pointer-events-none">
                                        <UploadIcon className="w-4 h-4" />
                                        Browse Files
                                    </div>
                                    <p className="text-xs text-gray-400 mt-4">
                                        PDF · 100 MB total limit
                                    </p>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        multiple
                                        accept={ACCEPTED_EXTENSIONS.join(",")}
                                        onChange={(e) => {
                                            if (e.target.files) addFiles(e.target.files);
                                            e.target.value = "";
                                        }}
                                        className="hidden"
                                    />
                                </div>
                            </div>

                            {/* Right panel — queued files */}
                            <div className="flex flex-col min-h-0">
                                <div className="flex items-center justify-between mb-3 flex-shrink-0">
                                    <h4 className="font-semibold text-base text-gray-900">
                                        Queued Files
                                    </h4>
                                    {hasFiles && (
                                        <span
                                            className={cn(
                                                "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold",
                                                isBatchTooLarge
                                                    ? "bg-rose-100 text-rose-700"
                                                    : "bg-blue-100 text-blue-700",
                                            )}
                                        >
                                            {formatFileSize(totalQueuedSize)} / {formatFileSize(MAX_TOTAL_SIZE)}
                                        </span>
                                    )}
                                </div>

                                {isBatchTooLarge && (
                                    <div className="flex items-center gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700 mb-3 flex-shrink-0">
                                        <AlertCircleIcon className="w-3.5 h-3.5 flex-shrink-0" />
                                        Total size {formatFileSize(totalQueuedSize)} exceeds the 100 MB limit. Remove some files to continue.
                                    </div>
                                )}

                                {!hasFiles ? (
                                    <div className="flex-1 flex items-center justify-center rounded-lg border border-dashed border-gray-200 p-8 min-h-[180px]">
                                        <div className="text-center">
                                            <FileTextIcon className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                                            <p className="text-sm text-gray-500">No files added yet.</p>
                                            <p className="text-xs text-gray-400 mt-1">
                                                Add PDF files to continue.
                                            </p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex-1 overflow-y-auto min-h-0 border border-gray-200 rounded-lg">
                                        {queuedFiles.map((entry, idx) => (
                                            <div
                                                key={entry.id}
                                                className={cn(
                                                    "flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors",
                                                    idx !== queuedFiles.length - 1 && "border-b border-gray-100",
                                                )}
                                            >
                                                <FileTextIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-gray-800 truncate">
                                                        {entry.file.name}
                                                    </p>
                                                    <p className="text-xs text-gray-400">
                                                        {formatFileSize(entry.file.size)}
                                                    </p>
                                                    {entry.errorMessage && (
                                                        <p className="text-xs text-rose-500 mt-0.5">
                                                            {entry.errorMessage}
                                                        </p>
                                                    )}
                                                </div>
                                                {entry.errorMessage ? (
                                                    <AlertCircleIcon className="w-4 h-4 text-rose-500 flex-shrink-0" />
                                                ) : (
                                                    <CheckCircle2Icon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                                                )}
                                                <button
                                                    onClick={() => removeQueued(entry.id)}
                                                    className="p-1 text-gray-400 hover:text-gray-600 rounded flex-shrink-0"
                                                >
                                                    <XIcon className="w-4 h-4" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100 flex-shrink-0">
                        <button
                            onClick={clearFiles}
                            disabled={!hasFiles}
                            className="text-sm text-gray-500 hover:text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                            Clear all
                        </button>
                        <div className="flex gap-3">
                            <Button
                                variant="outline"
                                onClick={() => { resetUploadModal(); setUploadOpen(false); }}
                            >
                                Cancel
                            </Button>
                            <Button onClick={handleUpload} disabled={!canUpload}>
                                <UploadIcon className="w-4 h-4" />
                                Upload{" "}
                                {queuedFiles.filter((f) => !f.errorMessage).length > 0
                                    ? `(${queuedFiles.filter((f) => !f.errorMessage).length})`
                                    : ""}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* ===== Delete confirmation ===== */}
            <Dialog
                open={deleteTarget !== null}
                onOpenChange={(open) => {
                    if (!open && !deleting) {
                        setDeleteTarget(null);
                        setDeleteError(null);
                    }
                }}
            >
                <DialogContent className="sm:max-w-[440px] gap-4">
                    <DialogHeader className="space-y-3">
                        <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-rose-100">
                                <Trash2Icon className="h-5 w-5 text-rose-600" />
                            </div>
                            <DialogTitle className="text-lg font-semibold">
                                Delete document?
                            </DialogTitle>
                        </div>
                    </DialogHeader>

                    <p className="-mt-2 text-sm text-muted-foreground">
                        <span className="font-medium text-foreground break-words">
                            {deleteTarget?.name}
                        </span>{" "}
                        will be permanently removed from your knowledge base and won't be
                        used in future AI evaluations. Completed results are not affected.
                    </p>

                    {deleteError && (
                        <div className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2">
                            <AlertCircleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-rose-600" />
                            <p className="text-sm text-rose-700">{deleteError}</p>
                        </div>
                    )}

                    <div className="mt-2 flex justify-end gap-2">
                        <Button
                            variant="outline"
                            disabled={deleting}
                            onClick={() => {
                                setDeleteTarget(null);
                                setDeleteError(null);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            disabled={deleting}
                            onClick={() => { if (deleteTarget) handleDelete(deleteTarget); }}
                        >
                            {deleting ? (
                                <>
                                    <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                                    Deleting…
                                </>
                            ) : (
                                "Delete"
                            )}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Shared document drawer — Preview + Extracted Information */}
            <KnowledgeDocDrawer
                doc={previewTarget}
                onClose={() => setPreviewTarget(null)}
            />
        </>
    );
}

export default function TrainingKnowledgeBasePage() {
    return (
        <TrainingAuthLayout>
            <KnowledgeBasePageContent />
        </TrainingAuthLayout>
    );
}
